// Age from date of birth for the Final Client Details Form (spec §2.3 / §6).
//
// Age is DERIVED, never entered and never persisted: the form's Age field is
// read-only, recomputed from date_of_birth, and skipped in the tab order. There
// is deliberately no `age` column on the traveller table.
//
// All arithmetic uses UTC accessors — a local-time `getFullYear()` would shift
// the birthday by a day for anyone west of UTC and make the "day before /
// day after a birthday" acceptance criterion flaky.

/** Oldest plausible traveller. Beyond this the date is treated as bad data. */
const MAX_PLAUSIBLE_AGE = 130;

const parseIsoDateParts = (
  value: string
): { year: number; month: number; day: number } | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  // Reject a date the calendar does not have (e.g. 2026-02-31): round-tripping
  // through UTC normalises it, so a mismatch means the input was invalid.
  const asUtc = new Date(Date.UTC(year, month - 1, day));
  if (
    asUtc.getUTCFullYear() !== year ||
    asUtc.getUTCMonth() !== month - 1 ||
    asUtc.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
};

/**
 * Age in whole years, or null when the date of birth is absent, unparseable,
 * in the future, or implausible.
 *
 * Per spec §11 an out-of-range date renders an EMPTY age and flags the field —
 * it never guesses and never shows a negative number.
 */
export const calculateTravellerAge = (
  dateOfBirth: string | null | undefined,
  now: Date = new Date()
): number | null => {
  if (!dateOfBirth) return null;
  const dob = parseIsoDateParts(dateOfBirth);
  if (!dob) return null;

  const todayYear = now.getUTCFullYear();
  const todayMonth = now.getUTCMonth() + 1;
  const todayDay = now.getUTCDate();

  let age = todayYear - dob.year;
  // Not had this year's birthday yet.
  if (
    todayMonth < dob.month ||
    (todayMonth === dob.month && todayDay < dob.day)
  ) {
    age -= 1;
  }

  if (age < 0) return null;
  if (age > MAX_PLAUSIBLE_AGE) return null;
  return age;
};

/**
 * What the Age field displays. Falls back to the age XT already held when the
 * itinerary has no date of birth (spec §5: "Shown as given when no date of
 * birth exists; the date of birth is still requested, and once entered it takes
 * over the calculation").
 */
export const displayTravellerAge = (
  input: { dateOfBirth?: string | null; recordedAge?: string | null },
  now: Date = new Date()
): string => {
  const computed = calculateTravellerAge(input.dateOfBirth, now);
  if (computed !== null) return String(computed);
  return (input.recordedAge ?? '').trim();
};
